import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { MAX_QIT_IDS_FROM_PARAMS } from 'src/redux/slices/Interview/types';
import type { QIT } from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';

interface UseFocusModeReturn {
  focusedQitIds: Set<number> | null;
  isFocusMode: boolean;
  clearFocus: () => void;
  getVisibleQitIds: (
    baseVisibleIds: Set<number>,
    allQits: QIT[]
  ) => Set<number>;
}

export function useQuestionnaireFocus(): UseFocusModeReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const focusedQitIds = useMemo(() => {
    const qitsParam = searchParams.get('qits');
    if (!qitsParam) {
      return null;
    }
    const ids = qitsParam
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .slice(0, MAX_QIT_IDS_FROM_PARAMS);
    return ids.length > 0 ? new Set(ids) : null;
  }, [searchParams]);

  const isFocusMode = focusedQitIds !== null;

  const clearFocus = useCallback((): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('qits');
      return next;
    });
  }, [setSearchParams]);

  const getVisibleQitIds = useCallback(
    (baseVisibleIds: Set<number>, allQits: QIT[]): Set<number> => {
      if (!focusedQitIds) {
        return baseVisibleIds;
      }

      const result = new Set<number>();

      for (const id of focusedQitIds) {
        if (baseVisibleIds.has(id)) {
          result.add(id);
        }
      }

      for (const qit of allQits) {
        if (
          qit.parent_qit_id &&
          focusedQitIds.has(qit.parent_qit_id) &&
          baseVisibleIds.has(qit.id)
        ) {
          result.add(qit.id);
        }
      }

      return result;
    },
    [focusedQitIds]
  );

  return { focusedQitIds, isFocusMode, clearFocus, getVisibleQitIds };
}
